import React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { useMobileEdgeChromeStore } from "../../stores/mobileEdgeChromeStore";
import { PORTAL_ABOVE_ALL_PANELS_Z } from "../../utils/panelZIndex";
import Tooltip from "./Tooltip";

function DropdownMenu({ onOpenChange, ...props }) {
  const handleOpenChange = React.useCallback(
    (open) => {
      useMobileEdgeChromeStore.getState().actions.notifyMenuOverlayOpenChange(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );
  return <DropdownMenuPrimitive.Root {...props} onOpenChange={handleOpenChange} />;
}
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuSubTrigger = React.forwardRef(({ children, className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={["menu-item text-gray-100", className].filter(Boolean).join(" ")}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuContent = React.forwardRef(({ children, style, className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      className={[
        "menu-selection-root",
        "min-w-[8rem] overflow-hidden rounded-md border border-gray-700 p-1 shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundColor: "var(--panel-surface)",
        backdropFilter: "blur(10px)",
        ...style,
        zIndex: PORTAL_ABOVE_ALL_PANELS_Z,
      }}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Content>
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuSubContent = React.forwardRef(({ children, style, className, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={[
        "menu-selection-root",
        "min-w-[8rem] overflow-hidden rounded-md border border-gray-700 p-1 shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundColor: "var(--panel-surface)",
        backdropFilter: "blur(10px)",
        ...style,
        zIndex: PORTAL_ABOVE_ALL_PANELS_Z,
      }}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubContent>
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuItem = React.forwardRef(({ children, className, title, ...props }, ref) => {
  const item = (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={["menu-item text-gray-100", className].filter(Boolean).join(" ")}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );

  if (!title) return item;

  return (
    <Tooltip content={title} side="right" fullWidth>
      {item}
    </Tooltip>
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={`-mx-1 my-1 h-px bg-gray-600 ${className}`}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
